import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { hojeBR } from '../../utils/data';
import { ITENS_SIMPLES, GRUPOS, moduloBloqueado } from '../../config/menus';
import { abrirComSessao } from '../../utils/sessao';
import useRecorrenciaHabilitada from '../../hooks/useRecorrenciaHabilitada';
import { usePermissoes } from '../../contexts/PermissoesContext';
import { useAuth } from '../../contexts/AuthContext';
import SeletorOrganizacao from '../common/SeletorOrganizacao';
import PainelLiberacoes from '../common/PainelLiberacoes';
import { hostTemPainel } from '../../services/painelApi';
import { BRAND_NOME } from '../../brand';
import styles from './Sidebar.module.css';

const hhmmBR = iso => {
  const d = new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000);
  return d.toISOString().slice(11, 16);
};

// Relógio de Brasília em HH:MM:SS, sem depender do fuso do navegador (mesmo
// ajuste -3h fixo usado em todo o resto do sistema).
function relogioBR() {
  const d = new Date(Date.now() - 3 * 60 * 60 * 1000);
  return d.toISOString().slice(11, 19);
}

// "em 15 min" / "Agora" / "atrasado 5 min" — recalculado a cada render (o
// painel re-renderiza a cada segundo por causa do relógio vivo, então isso
// nunca fica desatualizado sem precisar de um efeito próprio).
function tempoRelativo(iso) {
  const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (diffMin <= 1 && diffMin >= -5) return 'Agora';
  if (diffMin < -5) return `atrasado ${Math.abs(diffMin)} min`;
  if (diffMin < 60) return `em ${diffMin} min`;
  const h = Math.floor(diffMin / 60), m = diffMin % 60;
  return `em ${h}h${m > 0 ? `${m}min` : ''}`;
}

const MINUTOS_JANELA_PADRAO_SIDEBAR = 12 * 60; // 08h–20h, mesmo padrão da Agenda

function iniciais(nome) {
  if (!nome) return '?';
  const p = nome.trim().split(' ');
  return (p[0][0] + (p[1] ? p[1][0] : '')).toUpperCase();
}

// Sino de notificações + cadeado de liberações de senha pendentes — morava
// no topo do Header, mudou pra cá pra a barra de cima do conteúdo só existir
// quando tem aba aberta (ver Header.jsx).
function NotificacoesRow() {
  const { usuario } = useAuth();
  // admin, gerente, ou qualquer perfil customizado com "libera senha admin"
  // marcado em Perfis de Usuário (calculado no login — ver authController.js)
  const ehAdmin = !!usuario?.pode_gerenciar_liberacoes;
  const [pendLib, setPendLib] = useState(0);
  const [painelLib, setPainelLib] = useState(false);

  const atualizarPendLib = useCallback(async () => {
    try { const { data } = await api.get('/liberacoes/pendentes/contagem'); setPendLib(data.total || 0); }
    catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    if (!ehAdmin) return;
    atualizarPendLib();
    const t = setInterval(atualizarPendLib, 15000);
    return () => clearInterval(t);
  }, [ehAdmin, atualizarPendLib]);

  // Alerta sonoro: enquanto houver liberação pendente, bipe a cada 20s — Web
  // Audio API (sem arquivo); navegador pode silenciar até a 1ª interação do
  // usuário na página, depois disso toca normalmente.
  const audioCtxRef = useRef(null);
  const tocarAlerta = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const t0 = ctx.currentTime;
      [[880, 0], [660, 0.18]].forEach(([freq, dt]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t0 + dt);
        gain.gain.exponentialRampToValueAtTime(0.25, t0 + dt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0 + dt);
        osc.stop(t0 + dt + 0.18);
      });
    } catch (_) { /* áudio indisponível: segue só o badge visual */ }
  }, []);

  const temPendLib = ehAdmin && pendLib > 0;
  useEffect(() => {
    if (!temPendLib) return;
    tocarAlerta();
    const t = setInterval(tocarAlerta, 20000);
    return () => clearInterval(t);
  }, [temPendLib, tocarAlerta]);

  // Painel de Clientes — login PRÓPRIO (painel_usuarios), sem relação com a
  // sessão do ERP (entrar aqui não elimina o login de lá, são credenciais
  // diferentes) — só um atalho pra não precisar digitar a URL. Só aparece
  // nas instâncias que fazem a gestão dos sites (ver hostTemPainel).
  const mostraPainelClientes = hostTemPainel();

  return (
    <div className={styles.notifRow}>
      {ehAdmin && (
        <button className={styles.notifBtn} onClick={() => setPainelLib(true)}
          title={pendLib > 0
            ? `${pendLib} solicitação(ões) de liberação aguardando você`
            : 'Solicitações de liberação'}>
          🔓
          {pendLib > 0 && <span className={styles.notifBadge}>{pendLib}</span>}
        </button>
      )}
      {painelLib && (
        <PainelLiberacoes onClose={() => setPainelLib(false)} onChange={atualizarPendLib} />
      )}
      <button className={styles.notifBtn} title="Notificações">🔔</button>
      {mostraPainelClientes && (
        // Abre como aba interna (TabContext#ROTAS), igual Produtos/Clientes/
        // Contas a Receber — não é mais nova aba do navegador.
        <NavLink to="/painel-clientes" className={styles.notifBtn} title="Painel de Clientes (login próprio)">
          🧑‍💼
        </NavLink>
      )}
    </div>
  );
}

// Painel "Agenda Hoje" — só pra organizações cujo segmento usa Agendamento
// (petshop, clínica, oficina...). Mostra quantos atendimentos tem hoje e o
// próximo da fila, com atalho pra já iniciar sem precisar abrir a Agenda.
function PainelAgendaHoje() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null); // null = não carregou ainda
  const [relogio, setRelogio] = useState(relogioBR);

  const carregar = useCallback(() => {
    if (!usuario?.mostra_agendamento) return;
    const hoje = hojeBR();
    api.get('/vendas/pedidos', {
      params: { agendado_de: `${hoje}T00:00:00-03:00`, agendado_ate: `${hoje}T23:59:59-03:00`, limite: 200 },
    }).then(r => {
      const lista = (r.data || []).filter(p => p.status !== 'cancelado');
      const proximo = lista
        .filter(p => p.status === 'agendado' || p.status === 'em_atendimento')
        .sort((a, b) => a.agendado_para.localeCompare(b.agendado_para))[0] || null;
      const minutosOcupados = lista.reduce((s, p) => s + (parseInt(p.duracao_min_estimada) || 30), 0);
      setDados({ total: lista.length, proximo, minutosOcupados });
    }).catch(() => setDados({ total: 0, proximo: null, minutosOcupados: 0 }));
  }, [usuario?.mostra_agendamento]);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 60000); // acompanha o "próximo" mudando ao longo do dia
    return () => clearInterval(t);
  }, [carregar]);

  // Relógio vivo — sensação de "sistema ativo" pedida pela usuária; o
  // painel inteiro re-renderiza a cada segundo, o que também mantém o
  // "tempoRelativo" do próximo atendimento sempre atualizado de graça.
  useEffect(() => {
    const t = setInterval(() => setRelogio(relogioBR()), 1000);
    return () => clearInterval(t);
  }, []);

  const iniciar = async (e, pedido) => {
    e.stopPropagation();
    try {
      await api.patch(`/vendas/pedidos/${pedido.id}/iniciar-atendimento`);
      carregar();
    } catch { /* usuário confirma/corrige na tela cheia da Agenda */ }
  };

  if (!usuario?.mostra_agendamento || !dados) return null;

  const pctOcupacao = Math.min(100, Math.round((dados.minutosOcupados / MINUTOS_JANELA_PADRAO_SIDEBAR) * 100));
  const corOcupacao = pctOcupacao >= 80 ? '#f87171' : pctOcupacao >= 50 ? '#fbbf24' : '#4ade80';

  return (
    <div className={styles.painelAgenda} onClick={() => navigate('/agenda')} role="button" tabIndex={0}>
      <div className={styles.painelAgendaCabecalho}>
        <span className={styles.painelAgendaTitulo}>📅 Agenda — Hoje</span>
        <span className={styles.painelAgendaRelogio}>{relogio}</span>
      </div>
      {dados.total === 0 ? (
        <div className={styles.painelAgendaVazio}>Nenhum agendamento hoje.</div>
      ) : (
        <>
          <div className={styles.painelAgendaTotal}>
            {dados.total} agendamento{dados.total > 1 ? 's' : ''}
          </div>

          <div className={styles.painelAgendaOcupacao}>
            <div className={styles.painelAgendaOcupacaoTopo}>
              <span>Ocupação</span>
              <strong>{pctOcupacao}%</strong>
            </div>
            <div className={styles.painelAgendaBarraFundo}>
              <div className={styles.painelAgendaBarraPreench} style={{ width: `${pctOcupacao}%`, background: corOcupacao }} />
            </div>
          </div>

          {dados.proximo && (
            <div className={styles.painelAgendaProximo}>
              <div className={styles.painelAgendaProximoLabel}>⏰ Próximo — {tempoRelativo(dados.proximo.agendado_para)}</div>
              <div className={styles.painelAgendaHora}>{hhmmBR(dados.proximo.agendado_para)}</div>
              <div className={styles.painelAgendaQuem}>👤 {dados.proximo.cliente_nome || '—'}</div>
              {dados.proximo.participante_nome && (
                <div className={styles.painelAgendaQuem}>🐾 {dados.proximo.participante_nome}</div>
              )}
              {dados.proximo.status === 'agendado' ? (
                <button className={styles.painelAgendaBtn} onClick={e => iniciar(e, dados.proximo)}>
                  ▶️ Iniciar
                </button>
              ) : (
                <span className={styles.painelAgendaEmAtend}>🔵 Em atendimento</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GrupoMenu({ grupo, badge }) {
  const location = useLocation();
  const ativo = grupo.prefixo.some(p => location.pathname.startsWith(p));
  const [aberto, setAberto] = useState(ativo);

  return (
    <div>
      <button
        className={`${styles.grupoBtn} ${ativo ? styles.grupoBtnAtivo : ''}`}
        onClick={() => setAberto(a => !a)}
        aria-expanded={aberto}
      >
        <span className={styles.icon}>{grupo.icon}</span>
        <span className={styles.grupoLabel}>{grupo.label}</span>
        {badge > 0 && (
          <span title={`${badge} Nota(s) Técnica(s) SEFAZ pendente(s)`} style={{
            background: '#f59e0b', color: '#fff', borderRadius: 9,
            fontSize: 10.5, fontWeight: 700, padding: '1px 6px', marginRight: 4,
          }}>
            {badge}
          </span>
        )}
        <span className={styles.chevron}>{aberto ? '▾' : '▸'}</span>
      </button>

      {aberto && (
        <div className={styles.subMenu}>
          {grupo.filhos.map(({ to, label, end, novaAba }) =>
            novaAba ? (
              <button
                key={to}
                className={styles.subLink}
                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => abrirComSessao(to)} // guia nova herda a organização DESTA guia
              >
                {label}
              </button>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `${styles.subLink} ${isActive ? styles.subLinkAtivo : ''}`
                }
              >
                {label}
              </NavLink>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ gaveta = false, aberta = false }) {
  // Permissões do perfil (Administrativo → Perfis de Usuário) via contexto —
  // o mesmo que bloqueia URL digitada no TabWatcher e a rota do PDV.
  const { perms, pronto } = usePermissoes();
  const { usuario, logout } = useAuth();
  const recorrenciaHabilitada = useRecorrenciaHabilitada();

  const tudoLiberado = !pronto || !perms || perms.admin || perms.menus?.includes('*');

  // Contador de NTs SEFAZ pendentes no menu Administrativo (403 = perfil sem
  // acesso → badge simplesmente não aparece)
  const [ntPendentes, setNtPendentes] = useState(0);
  useEffect(() => {
    if (!pronto) return;
    api.get('/notas-tecnicas/alertas')
      .then(r => setNtPendentes(r.data?.pendentes || 0))
      .catch(() => setNtPendentes(0));
  }, [pronto]);
  const veMenu = id => tudoLiberado || (perms.menus || []).includes(id);
  const veSub  = to => tudoLiberado || perms.submenus?.includes('*') || (perms.submenus || []).includes(to);

  const itensVisiveis  = ITENS_SIMPLES.filter(i => veMenu(i.id) && !moduloBloqueado(perms, i.to));
  const gruposVisiveis = GRUPOS
    // 'agendamento' é liberado pelo SEGMENTO da organização (Cadastro de
    // Organização → segmento habilita_agendamento), não pelo perfil — bar
    // não agenda horário, então nem admin de bar vê esse menu.
    .filter(g => g.id !== 'agendamento' || usuario?.mostra_agendamento)
    .filter(g => veMenu(g.id))
    .map(g => ({ ...g, filhos: g.filhos.filter(f => veSub(f.to) && !moduloBloqueado(perms, f.to)
      && (f.to !== '/vendas/recorrencia' || recorrenciaHabilitada)) }))
    .filter(g => g.filhos.length > 0);

  return (
    <aside className={`${styles.sidebar} ${gaveta ? styles.gaveta : ''} ${gaveta && aberta ? styles.gavetaAberta : ''}`}>
      <div className={styles.logo}>
        <span className={styles.logoBadge}>E+</span>
        <div>
          <span className={styles.logoText}>{BRAND_NOME}</span>
          <span className={styles.logoSub}>SaaS</span>
        </div>
      </div>

      <div className={styles.seletorOrgWrap}>
        <SeletorOrganizacao variant="sidebar" />
        <NotificacoesRow />
      </div>

      <PainelAgendaHoje />

      <nav className={styles.nav}>
        {gaveta && (
          <NavLink to="/mobile" className={styles.link}>
            <span className={styles.icon}>🏠</span>
            Menu inicial (mobile)
          </NavLink>
        )}
        {itensVisiveis.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `${styles.link} ${isActive ? styles.active : ''}`
            }
          >
            <span className={styles.icon}>{icon}</span>
            {label}
          </NavLink>
        ))}

        {gruposVisiveis.map(g => (
          <GrupoMenu key={g.id} grupo={g} badge={g.id === 'admin' ? ntPendentes : 0} />
        ))}

        {/* Painel de infraestrutura (backups) — só existe pro usuário
            oculto de suporte, não é feature de cliente. */}
        {usuario?.suporte_sistema && (
          <NavLink to="/painel-suporte"
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}>
            <span className={styles.icon}>🗄️</span>
            Backups (interno)
          </NavLink>
        )}
      </nav>

      <div className={styles.rodape}>
        <div className={styles.avatar}>{iniciais(usuario?.nome)}</div>
        <div className={styles.rodapeInfo}>
          <span className={styles.rodapeNome}>{usuario?.nome}</span>
          <span className={styles.rodapePerfil}>{usuario?.perfil}</span>
        </div>
        <button className={styles.rodapeSair} onClick={logout} title="Sair">⏻</button>
      </div>
    </aside>
  );
}
